import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import type { SiteSetting } from '@/types'

export default function Footer() {
  const [footerSettings, setFooterSettings] = useState<Record<string, string>>({})

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const [footerRes, seoRes] = await Promise.all([
          lumi.entities.site_settings.list({ filter: { category: 'footer' }, limit: 30 }),
          lumi.entities.site_settings.list({ filter: { category: 'seo' }, limit: 10 }),
        ])
        const map: Record<string, string> = {}
        ;(footerRes.list as SiteSetting[]).forEach(s => { map[s.key] = s.value })
        ;(seoRes.list as SiteSetting[]).forEach(s => { map[s.key] = s.value })
        setFooterSettings(map)
      } catch (e) {
        console.error('Erro ao carregar rodapé:', e)
      }
    }
    fetchFooter()
  }, [])

  const year = new Date().getFullYear()
  const siteName    = footerSettings['site_title']          || 'CARLOS FELIPE'
  const logoImage   = footerSettings['logo_image']          || ''
  const contact     = footerSettings['footer_contact']      || ''
  const copyright   = footerSettings['footer_copyright']    || `© ${year} ${siteName}. Todos os direitos reservados.`
  const about       = footerSettings['footer_about']        || ''
  const privacyLink = footerSettings['footer_privacy_link'] || '#'
  const termsLink   = footerSettings['footer_terms_link']   || '#'

  return (
    <footer className="w-full bg-gray-950 text-gray-400 mt-16 border-t border-gray-800/60">
      {/* Linha de acento superior */}
      <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-red-600 to-transparent opacity-60" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 mb-10">

          {/* ── Brand ── */}
          <div>
            <Link to="/" className="inline-flex items-center gap-2.5 mb-4 group">
              {logoImage ? (
                <img
                  src={logoImage}
                  alt={siteName}
                  className="h-10 w-auto object-contain max-w-[160px] opacity-90 group-hover:opacity-100 transition-opacity"
                />
              ) : (
                <>
                  <div className="w-9 h-9 bg-red-600 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-red-500 transition-colors shadow-sm">
                    <span className="text-white font-black text-sm tracking-tight">CF</span>
                  </div>
                  <span className="font-bold text-white text-base group-hover:text-red-400 transition-colors tracking-tight">{siteName}</span>
                </>
              )}
            </Link>
            {about && (
              <p className="text-sm text-gray-500 leading-relaxed">{about}</p>
            )}
          </div>

          {/* ── Contato ── */}
          {contact && (
            <div>
              <h3 className="text-white font-bold mb-4 text-sm uppercase tracking-widest flex items-center gap-2">
                <span className="w-4 h-0.5 bg-red-600 rounded-full inline-block" />
                Contato
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed whitespace-pre-line">{contact}</p>
            </div>
          )}

          {/* ── Links ── */}
          <div>
            <h3 className="text-white font-bold mb-4 text-sm uppercase tracking-widest flex items-center gap-2">
              <span className="w-4 h-0.5 bg-red-600 rounded-full inline-block" />
              Links
            </h3>
            <ul className="space-y-2.5">
              {[
                { href: privacyLink, label: 'Política de Privacidade' },
                { href: termsLink,   label: 'Termos de Uso' },
                { href: '/admin/login', label: 'Área Administrativa' },
              ].map(({ href, label }) => (
                <li key={label}>
                  <a
                    href={href}
                    className="text-sm text-gray-500 hover:text-red-400 transition-colors inline-flex items-center gap-1.5 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-gray-700 group-hover:bg-red-600 transition-colors" />
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── Rodapé inferior ── */}
        <div className="border-t border-gray-800/60 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-600">{copyright}</p>
          <p className="text-xs text-gray-700">
            Desenvolvido com{' '}
            <span className="text-red-600" aria-label="amor">♥</span>
          </p>
        </div>
      </div>
    </footer>
  )
}
